import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * Platform admin user management — invite, role-change, deactivate. The
 * invite flow uses the same admin_reset magic-link plumbing as the public
 * forgot-password (1h TTL there, 24h here so a fresh employee has time
 * to click during work hours).
 */

interface AdminUserRow {
  id: number;
  email: string;
  name: string | null;
  role: 'admin' | 'support' | 'readonly';
  active: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface InviteResp {
  user: AdminUserRow;
  emailed: boolean;
  invite_link?: string;
}

const ROLE_OPTIONS: AdminUserRow['role'][] = ['admin', 'support', 'readonly'];

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState<number | null>(null);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<AdminUserRow['role']>('admin');

  async function load() {
    try {
      const data = await api<{ users: AdminUserRow[] }>('/admin/admin-users');
      setUsers(data.users);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => {
    load();
    api<{ user: { id: number } }>('/auth/me').then((d) => setMe(d.user.id)).catch(() => {});
  }, []);

  function flash(msg: string, isErr = false) {
    if (isErr) setError(msg);
    else setInfo(msg);
    setTimeout(() => { setError(null); setInfo(null); }, 6000);
  }

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail) return;
    setBusy(true);
    try {
      const resp = await api<InviteResp>('/admin/admin-users', {
        method: 'POST',
        json: { email: inviteEmail.trim(), name: inviteName.trim() || undefined, role: inviteRole },
      });
      if (resp.emailed) {
        flash(`Invite sent to ${resp.user.email}. Link valid for 24 hours.`);
      } else if (resp.invite_link) {
        flash(`Created but email send failed. Copy this link: ${resp.invite_link}`, true);
      } else {
        flash(`Created ${resp.user.email}.`);
      }
      setInviteEmail(''); setInviteName(''); setInviteRole('admin');
      setShowInvite(false);
      load();
    } catch (e) {
      flash((e as Error).message || 'Invite failed', true);
    } finally {
      setBusy(false);
    }
  }

  async function patch(id: number, body: Partial<{ role: AdminUserRow['role']; active: boolean }>, label: string) {
    setBusy(true);
    try {
      await api(`/admin/admin-users/${id}`, { method: 'PATCH', json: body });
      flash(`${label} OK`);
      load();
    } catch (e) {
      flash(`${label}: ${(e as Error).message}`, true);
    } finally {
      setBusy(false);
    }
  }

  async function resendInvite(id: number, email: string) {
    if (!confirm(`Send a fresh 24h invite link to ${email}?`)) return;
    setBusy(true);
    try {
      const r = await api<{ emailed: boolean; invite_link?: string }>(
        `/admin/admin-users/${id}/resend-invite`,
        { method: 'POST', json: {} },
      );
      if (r.emailed) flash(`Resent to ${email}.`);
      else if (r.invite_link) flash(`Created but email send failed. Link: ${r.invite_link}`, true);
      else flash(`Resent.`);
    } catch (e) {
      flash(`Resend failed: ${(e as Error).message}`, true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>Platform admins</h1>
      <p className="subtitle">
        People with login access to admin.dealerbosses.com. Not the same as
        staff inside a tenant workspace.
      </p>
      {error && <div className="banner err">{error}</div>}
      {info && <div className="banner ok">{info}</div>}

      <div className="button-row" style={{ marginBottom: 12 }}>
        <button onClick={() => setShowInvite((s) => !s)}>
          {showInvite ? 'Cancel' : '+ Invite admin'}
        </button>
      </div>

      {showInvite && (
        <form onSubmit={invite} className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Invite a new admin</h3>
          <p className="muted small" style={{ marginTop: 0 }}>
            They'll get an email with a one-click link to set their password.
            Link is valid for 24 hours.
          </p>
          <div className="form-row" style={{ flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <label>Email</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@dealerbosses.com"
                required
              />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label>Name (optional)</label>
              <input
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Jane Operator"
              />
            </div>
            <div>
              <label>Role</label>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as AdminUserRow['role'])}>
                {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="button-row" style={{ marginTop: 12 }}>
            <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</button>
          </div>
        </form>
      )}

      {!users ? (
        <div className="muted">Loading…</div>
      ) : (
        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '8px 12px' }}>Email</th>
                <th style={{ padding: '8px 12px' }}>Name</th>
                <th style={{ padding: '8px 12px' }}>Role</th>
                <th style={{ padding: '8px 12px' }}>Status</th>
                <th style={{ padding: '8px 12px' }}>Last login</th>
                <th style={{ padding: '8px 12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === me;
                const pending = !u.last_login_at;
                return (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 12px' }}>{u.email} {isSelf && <span className="muted small">(you)</span>}</td>
                    <td style={{ padding: '8px 12px' }}>{u.name ?? <span className="muted">—</span>}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <select
                        value={u.role}
                        disabled={busy || isSelf}
                        onChange={(e) => patch(u.id, { role: e.target.value as AdminUserRow['role'] }, `Role → ${e.target.value}`)}
                      >
                        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span className={`pill ${u.active ? (pending ? 'warn' : 'ok') : 'muted'}`}>
                        {u.active ? (pending ? 'pending' : 'active') : 'deactivated'}
                      </span>
                    </td>
                    <td className="muted small" style={{ padding: '8px 12px' }}>
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : <span>never</span>}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div className="button-row" style={{ marginTop: 0 }}>
                        {u.active && pending && (
                          <button className="secondary" disabled={busy} onClick={() => resendInvite(u.id, u.email)}>
                            ✉ Resend invite
                          </button>
                        )}
                        {u.active && !isSelf && (
                          <button className="danger" disabled={busy} onClick={() => patch(u.id, { active: false }, 'Deactivate')}>
                            ⊘ Deactivate
                          </button>
                        )}
                        {!u.active && (
                          <button disabled={busy} onClick={() => patch(u.id, { active: true }, 'Reactivate')}>
                            ▶ Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
